// src/pages/configuracion/PreferenciasPage.tsx
import { Card, Button, Switch } from '@/components/ui';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { toast } from '@/components/ui/Toast';
import { RotateCcw, Sun, Moon, Monitor, Check } from 'lucide-react';
import { usePreferencesStore } from '@/stores/preferencesStore';
import type { Tema } from '@/types';
import { cn } from '@/lib/utils';

function applyTema(tema: Tema) {
  const shouldDark =
    tema === 'dark' ||
    (tema === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  document.documentElement.classList.toggle('dark', shouldDark);
}

const TEMAS: { value: Tema; label: string; icon: React.ReactNode }[] = [
  { value: 'light', label: 'Claro', icon: <Sun className="h-5 w-5" /> },
  { value: 'dark', label: 'Oscuro', icon: <Moon className="h-5 w-5" /> },
  { value: 'system', label: 'Sistema', icon: <Monitor className="h-5 w-5" /> },
];

const ZONAS_HORARIAS = [
  { value: 'America/Bogota', label: 'Bogota (GMT-5)' },
  { value: 'America/Mexico_City', label: 'Mexico City (GMT-6)' },
  { value: 'America/Lima', label: 'Lima (GMT-5)' },
  { value: 'America/Buenos_Aires', label: 'Buenos Aires (GMT-3)' },
  { value: 'America/Sao_Paulo', label: 'Sao Paulo (GMT-3)' },
  { value: 'America/New_York', label: 'New York (GMT-5)' },
  { value: 'Europe/Madrid', label: 'Madrid (GMT+1)' },
];

const FORMATOS_FECHA = [
  { value: 'DD/MM/YYYY', label: '31/12/2024 (DD/MM/YYYY)' },
  { value: 'MM/DD/YYYY', label: '12/31/2024 (MM/DD/YYYY)' },
  { value: 'YYYY-MM-DD', label: '2024-12-31 (ISO)' },
];

const FILAS_POR_PAGINA = [
  { value: '10', label: '10 filas' },
  { value: '20', label: '20 filas' },
  { value: '50', label: '50 filas' },
  { value: '100', label: '100 filas' },
];

export function PreferenciasPage() {
  const { preferencias, setPreferencia, setTema, resetPreferencias } = usePreferencesStore();

  const handleReset = () => {
    resetPreferencias();
    applyTema('system');
    toast.success('Preferencias restauradas a valores por defecto');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Preferencias</h1>
        <Button variant="outline" onClick={handleReset}>
          <RotateCcw className="h-4 w-4 mr-2" />
          Restaurar por Defecto
        </Button>
      </div>

      {/* Apariencia */}
      <Card className="p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Apariencia</h3>
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">Tema</label>
            <div className="flex gap-3">
              {TEMAS.map((tema) => (
                <button
                  key={tema.value}
                  type="button"
                  onClick={() => { setTema(tema.value); applyTema(tema.value); }}
                  className={cn(
                    'flex flex-col items-center gap-2 px-6 py-4 rounded-lg border-2 transition-all min-w-[100px]',
                    preferencias.tema === tema.value
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 hover:border-gray-300 text-gray-700'
                  )}
                >
                  {tema.icon}
                  <span className="text-sm font-medium">{tema.label}</span>
                  {preferencias.tema === tema.value && (
                    <Check className="h-4 w-4 text-blue-600" />
                  )}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between py-3 border-t">
            <div>
              <label className="font-medium text-gray-900">Sidebar colapsado por defecto</label>
              <p className="text-sm text-gray-500">Iniciar con el menu lateral minimizado</p>
            </div>
            <Switch
              checked={preferencias.sidebar_colapsado}
              onCheckedChange={(checked) => setPreferencia('sidebar_colapsado', checked)}
            />
          </div>
        </div>
      </Card>

      {/* Notificaciones */}
      <Card className="p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Notificaciones</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between py-2">
            <div>
              <label className="font-medium text-gray-900">Notificaciones por email</label>
              <p className="text-sm text-gray-500">Recibir alertas importantes por correo electrónico</p>
            </div>
            <Switch
              checked={preferencias.notificaciones_email}
              onCheckedChange={(checked) => setPreferencia('notificaciones_email', checked)}
            />
          </div>

          <div className="flex items-center justify-between py-2 border-t">
            <div>
              <label className="font-medium text-gray-900">Notificaciones push</label>
              <p className="text-sm text-gray-500">Notificaciones en tiempo real en el navegador</p>
            </div>
            <Switch
              checked={preferencias.notificaciones_push}
              onCheckedChange={(checked) => setPreferencia('notificaciones_push', checked)}
            />
          </div>

          <div className="flex items-center justify-between py-2 border-t">
            <div>
              <label className="font-medium text-gray-900">Sonido de notificaciones</label>
              <p className="text-sm text-gray-500">Reproducir sonido al recibir una notificación</p>
            </div>
            <Switch
              checked={preferencias.sonido_notificaciones}
              onCheckedChange={(checked) => setPreferencia('sonido_notificaciones', checked)}
            />
          </div>
        </div>
      </Card>

      {/* Regional */}
      <Card className="p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Regional y Formato</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Zona horaria</label>
            <Select
              value={preferencias.zona_horaria}
              onValueChange={(value) => setPreferencia('zona_horaria', value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ZONAS_HORARIAS.map((tz) => (
                  <SelectItem key={tz.value} value={tz.value}>
                    {tz.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Formato de fecha</label>
            <Select
              value={preferencias.formato_fecha}
              onValueChange={(value) => setPreferencia('formato_fecha', value as 'DD/MM/YYYY' | 'MM/DD/YYYY' | 'YYYY-MM-DD')}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FORMATOS_FECHA.map((f) => (
                  <SelectItem key={f.value} value={f.value}>
                    {f.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Formato de hora</label>
            <Select
              value={preferencias.formato_hora}
              onValueChange={(value) => setPreferencia('formato_hora', value as '12h' | '24h')}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="12h">12 horas (AM/PM)</SelectItem>
                <SelectItem value="24h">24 horas</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Filas por página en tablas</label>
            <Select
              value={String(preferencias.tabla_filas_por_pagina)}
              onValueChange={(value) => setPreferencia('tabla_filas_por_pagina', Number(value))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FILAS_POR_PAGINA.map((f) => (
                  <SelectItem key={f.value} value={f.value}>
                    {f.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      {/* Info de guardado */}
      <p className="text-sm text-gray-500 text-center">
        Las preferencias se guardan automáticamente en tu navegador
      </p>
    </div>
  );
}
